/**
 * 답변 본문에서 매칭 구절을 강조한다 — 판정은 서버(SQL mention_matches)가 했다. 여기서는 그 구절을 표시만.
 * 짧은 브랜드명의 오탐(본치과 ⊂ 서울본치과)을 사람이 눈으로 잡는 자리다.
 *
 * 우리 사이트 인용 칩도 함께 표시한다 — 칩이 붙은 **줄**(그 칩이 뒷받침하는 주장)을 연하게 칠하고 칩을 진하게.
 * 원문의 칩은 UI 세대에 따라 두 꼴이다:
 * - 옛 꼴(~10-08): `DDerma Clinic Guide+1` — 출처 이름(attribution) 앞에 **첫 글자가 한 번 더**
 *   (대소문자는 다를 수 있다 — `Hhanbangindex.com`), 묶음이면 뒤에 `+N`. 칩이 주장과 **같은 줄**에 붙는다.
 * - 새 꼴(10-09~, 261009-chatgpt-unauth-ui-change): 이름이 **한 줄을 통째로** 차지하고 묶음이면 다음 줄이 `+N`.
 *   칩이 뒷받침하는 주장은 **바로 앞 줄**이다.
 * 두 꼴 모두 "겹친 첫 글자" 또는 "줄 전체" 일 때만 칩으로 본다 — 문장 속 이름은 본문의 일반 언급일 수 있다.
 * ★ `+N` 묶음의 **둘째 이후** 출처는 원문에 이름이 없어 찾을 수 없다(chipsFound 로 가려 화면이 알린다).
 *   실측 2026-10-08: 우리 사이트 인용 340건 중 334건이 옛 꼴로 잡혔고 6건이 묶음 안이었다.
 *   실측 2026-10-21(로컬 복제본): 10-09 이후 959건 중 옛 꼴로는 **0건** — 화면이 전부 "못 찾음"을 띄웠다.
 *   새 꼴로 817건이 잡히고 142건이 묶음 안이다.
 */
import { Fragment, type ReactNode } from 'react';

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const norm = (s: string) => s.trim().toLowerCase();
const PLUS_LINE = /^\s*\+\d+\s*$/;

/** 옛 꼴 — `첫글자 + 이름 (+N)?` */
function chipPattern(names: readonly string[]): string | null {
  const alts = [...new Set(names.filter((n) => n.length > 0))]
    .sort((a, b) => b.length - a.length)
    .map((n) => `${esc(n[0]!)}${esc(n)}`);
  return alts.length ? `(?:${alts.join('|')})(?:\\+\\d+)?` : null;
}

/** 새 꼴 — 이름만 있는 줄 */
function chipLineNames(names: readonly string[]): Set<string> {
  return new Set(names.map(norm).filter((n) => n.length > 0));
}

/** 원문에서 칩으로 찾은 출처 이름 — 인용 목록이 "본문에 칩이 안 보인다"를 알리는 데 쓴다 */
export function chipsFound(text: string, names: readonly string[]): Set<string> {
  const found = new Set<string>();
  const lines = new Set(text.split('\n').map(norm));
  for (const n of new Set(names)) {
    const p = chipPattern([n]);
    if ((p && new RegExp(p, 'i').test(text)) || (norm(n) && lines.has(norm(n)))) found.add(n);
  }
  return found;
}

export function MentionSnippet({
  text,
  snippets,
  ourChips = [],
}: {
  text: string;
  snippets: string[];
  /** 우리 사이트 인용의 출처 이름(attribution) */
  ourChips?: string[];
}) {
  const chip = chipPattern(ourChips);
  const lineNames = chipLineNames(ourChips);
  const mention = snippets.length
    ? [...snippets]
        .sort((a, b) => b.length - a.length)
        .map(esc)
        .join('|')
    : null;
  if (!chip && !mention) return <p className="whitespace-pre-wrap text-sm leading-relaxed">{text}</p>;
  // 칩을 먼저 둔다 — 칩 이름 안에 브랜드명이 들어 있어도 칩으로 본다
  const re = new RegExp(
    [chip && `(?<chip>${chip})`, mention && `(?<mention>${mention})`].filter(Boolean).join('|'),
    'gi',
  );
  const chipLine = chip ? new RegExp(chip, 'i') : null;

  const lines = text.split('\n');
  // 새 꼴: 칩 줄(+ 뒤따르는 `+N` 줄)은 통째로 칩, 그 앞의 비어 있지 않은 줄이 뒷받침하는 주장이다
  const chipRows = new Set<number>();
  const claimRows = new Set<number>();
  lines.forEach((line, i) => {
    if (!lineNames.has(norm(line))) return;
    chipRows.add(i);
    if (PLUS_LINE.test(lines[i + 1] ?? '')) chipRows.add(i + 1);
    for (let j = i - 1; j >= 0; j--) {
      const prev = lines[j]!;
      if (!prev.trim() || PLUS_LINE.test(prev) || lineNames.has(norm(prev))) continue;
      claimRows.add(j);
      break;
    }
  });

  const mark = (line: string): ReactNode[] => {
    const out: ReactNode[] = [];
    let last = 0;
    for (const m of line.matchAll(re)) {
      if (m.index > last) out.push(line.slice(last, m.index));
      out.push(
        m.groups?.chip ? (
          <mark
            key={m.index}
            className="rounded bg-emerald-200 px-0.5 font-medium text-emerald-900"
            title="우리 사이트 인용"
          >
            {m[0]}
          </mark>
        ) : (
          <mark key={m.index}>{m[0]}</mark>
        ),
      );
      last = m.index + m[0].length;
    }
    if (last < line.length) out.push(line.slice(last));
    return out;
  };

  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed">
      {lines.map((line, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: 분할 결과는 순서가 곧 정체다
        <Fragment key={i}>
          {i > 0 ? '\n' : null}
          {chipRows.has(i) ? (
            <mark className="rounded bg-emerald-200 px-0.5 font-medium text-emerald-900" title="우리 사이트 인용">
              {line}
            </mark>
          ) : claimRows.has(i) || chipLine?.test(line) ? (
            <span className="rounded bg-emerald-50 box-decoration-clone">{mark(line)}</span>
          ) : (
            mark(line)
          )}
        </Fragment>
      ))}
    </p>
  );
}
